import { useContext, useRef } from 'react';
import _ from 'lodash';

import ComparisonContext from 'contexts/ComparisonContext';

import { followsReportComparison, getBlockComparison } from './blockComparison';
import { isLegacyMatrixTable } from './buildLegacyMatrixDefinition';
import {
  getTableCapabilities,
  isTableFollowingReport,
} from './buildTableDefinition';

type ComparisonContent = Parameters<typeof getBlockComparison>[0];

// The report's comparison as a Table following it draws it: one whose
// layout only compares each interval with the one before compares with the
// previous period, as it would choosing its own
const toTableComparison = ({
  content,
  comparison,
}: {
  content: ComparisonContent;
  comparison: PersistedComparisonType | undefined;
}): PersistedComparisonType | undefined =>
  comparison &&
  isTableFollowingReport(content) &&
  comparison.compareType === 'lastYear' &&
  !getTableCapabilities(content).canCompareLastYear
    ? { compareType: 'previous' }
    : comparison;

// The comparison a block draws: the report's (the comparison around it) when
// it follows the report's, else its own. On a dashboard or report view, the
// gadget's comparison around it wins when it's the same type, so a viewer's
// "Compare by" applies as it does to a classic gadget. Only date
// comparison and pace tables draw a compareTo (the legacy matrices they
// save as), and only that; a smart card doesn't. The same object while its
// contents don't change: the query refetches whenever it changes.
const useRenderedComparison = ({
  content,
  isOnView = false,
}: {
  content: ComparisonContent;
  isOnView?: boolean;
}): PersistedComparisonType | undefined => {
  const { currentComparison: around } = useContext(ComparisonContext);
  const own = getBlockComparison(content);
  const chosen = followsReportComparison(content)
    ? toTableComparison({ content, comparison: around })
    : own && isOnView && around && around.compareType === own.compareType
      ? around
      : own;
  const rendered =
    chosen &&
    (chosen.compareType === 'compareTo') === isLegacyMatrixTable(content)
      ? chosen
      : undefined;

  const ref = useRef(rendered);
  if (!_.isEqual(ref.current, rendered)) {
    ref.current = rendered;
  }
  return ref.current;
};

export default useRenderedComparison;
